import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withHouseholdContextAdapter } from 'Context/SimpleHouseholdContext';
import { withUserContextAdapter } from 'Context/UserContext';
import Dialog from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Dialog';
import DialogContent from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogContent';
import {
  CardElement as StripeCardElement,
  injectStripe,
} from 'react-stripe-elements';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Checkbox from 'Components/UI/BehavioralFormElement/Checkbox/GreenCheckbox';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import Button from 'Components/UI/Button';
import TextInput from 'Components/UI/BehavioralFormElement/Text/Input';
import { signupCompleteUri, dashboardUri } from 'Consts/clientUris';
import history from 'State/history';

import './index.css';
import numeral from 'numeral';
import { withStyles } from '@material-ui/styles';
import { properCase } from 'Helpers/Formats';
import { PALETTE } from 'Theme';

const BUSINESS_ERR_MSG = /.+Message:/g;

const styles = {
  header: {
    padding: '1rem 0',
  },
  lineItem: {
    lineHeight: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    '& span:first-child': {
      color: PALETTE.facet.darkGray,
      fontWeight: 'bold',
    },
    '& span:last-child': {
      color: PALETTE.facet.primaryGray,
    },
  },
  lineItemTotal: {
    fontSize: '1.2rem',
  },
  nextCharge: {
    color: PALETTE.facet.primaryGray,
  },
};

class PaymentModal extends Component {
  constructor(props) {
    super(props);

    this.onCompleteSignUp = this.onCompleteSignUp.bind(this);
    this.onReportError = this.onReportError.bind(this);
    this.updateState = this.updateState.bind(this);
    this.clientName = this.clientName.bind(this);

    this.state = {
      authorized: false,
      name: this.clientName(),
      processing: false,
      billingInformationCollection: null,
    };
  }

  componentDidMount() {
    const { householdResource } = this.props;

    if (householdResource) {
      this.loadBillingInformation();
    }
  }

  componentDidUpdate(prevProps) {
    const { householdResource } = this.props;
    const prevHouseholdResource = prevProps.householdResource;

    if (householdResource && !prevHouseholdResource) {
      this.loadBillingInformation();
    }
  }

  onCompleteSignUp() {
    const {
      stripe: { createToken },
      householdId,
    } = this.props;
    const { postPaymentSource } = this.context;
    const { onReportError } = this;
    const { name } = this.state;
    const names = name.split(' ');
    if (names.length < 2) {
      onReportError(
        new Error('Invalid name'),
        'A valid name should contain at least a first and last.',
      );
      return;
    }
    for (let i = 0; i < names.length; i++) {
      if (names[i].trim().length < 1) {
        onReportError(
          new Error('Invalid name'),
          'Remove extra spaces from the name',
        );
        return;
      }
    }
    this.setState({ processing: true }, () => {
      createToken({ name })
        .then(({ token, error }) => {
          if (typeof error === 'object' && 'message' in error) {
            onReportError(error, error.message);
          } else {
            postPaymentSource(householdId, {
              attributes: {
                token: token.id,
              },
            })
              .then(() => {
                history.replace(signupCompleteUri);
              })
              .catch((err) => {
                let errMessage = '';
                try {
                  errMessage = err.response.body.errors[0].detail.replace(
                    BUSINESS_ERR_MSG,
                    '',
                  );
                  if (!errMessage) {
                    errMessage = JSON.parse(err.response.body.errors[0].detail)
                      .message;
                  } // Our server is passing on stripe's error message in JSON form. So we must parse it to get the message.
                } catch (e) {
                  errMessage = 'There was a problem processing your payment information. Please speak to your advisor before resubmitting.';
                }
                onReportError(err, errMessage);
              });
          }
        })
        .catch((error) => {
          if (typeof error === 'object' && 'message' in error) {
            onReportError(error, error.message);
          }
        });
    });
  }

  onReportError(err, message) {
    displayErrorMessage(err, message);
    this.setState({ processing: false });
  }

  async loadBillingInformation() {
    const { householdResource } = this.props;

    try {
      const res = await householdResource.relationship('billingInformation').link('related').get();
      this.setState(() => ({
        billingInformationCollection: res,
      }));
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }

  isLoading() {
    const { householdResource } = this.props;
    const { billingInformationCollection } = this.state;

    return !householdResource || !billingInformationCollection;
  }

  clientName() {
    const { UserContext } = this.props;
    const { user } = UserContext;
    return `${user.firstName} ${user.lastName}`;
  }

  updateState(name, value) {
    this.setState({
      [name]: value,
    });
  }

  renderContent() {
    const { classes, householdResource } = this.props;
    const {
      authorized, name, processing, billingInformationCollection,
    } = this.state;

    const [billingInformationResource] = billingInformationCollection;
    const proratedCharge = billingInformationCollection.included().findResource(billingInformationResource.relationship('proratedFeeCharge')?.data);
    const subscriptionCharge = billingInformationCollection.included().findResource(billingInformationResource.relationship('subscriptionFeeCharge')?.data);
    const lineItems = [
      proratedCharge,
      subscriptionCharge,
    ].filter(c => !!c)
      .map(c => ({
        charge: c,
        discounts: (c.relationship('appliedDiscounts')?.data || [])
          .map(rio => billingInformationCollection.included().findResource(rio))
          .filter(r => !!r)
          .map(d => ({
            appliedDiscount: d,
            discount: billingInformationCollection.included().findResource(d.relationship('discount')?.data) || {},
          })),
      }));

    if (lineItems.length === 0) {
      displayErrorMessage(
        new Error(`billing information for household ${householdResource.id()} was empty`),
        'Failed to retrieve billing information',
      );
      return null;
    }

    const dueNow = lineItems[0]; // Prorated charge for quarterly, subscription charge for all others
    const dueNext = lineItems[1]; // Will be undefined for non-quarterly since there is only a subscription charge

    return (
      <Fragment>
        <div className={classes.lineItem}>
          <span>Billing Frequency</span>
          <span>{properCase(billingInformationResource.attr('frequency'))}</span>
        </div>
        <hr />
        <div className={classes.lineItem}>
          <span>Facet Membership Fee</span>
          <span>{numeral(dueNow.charge.attr('amountInCentsBeforeDiscounts')).divide(100).format('$0,0[.]00')}</span>
        </div>
        {dueNow.discounts.map(d => (
          <div className={classes.lineItem}>
            <span>{d.discount.attr('name') || 'Coupon'}</span>
            <span>{numeral(d.appliedDiscount.attr('amountOffInCents')).divide(-100).format('$0,0[.]00')}</span>
          </div>
        ))}
        <hr />
        <div className={`${classes.lineItem} ${classes.lineItemTotal}`}>
          <span>Due Today</span>
          <span>{numeral(dueNow.charge.attr('amountInCentsAfterDiscounts')).divide(100).format('$0,0[.]00')}</span>
        </div>
        <br />
        {/* We don't currently have the backend logic to calculate arbitrary numbers of billing cycles in the future,
          * so we're only displaying the next charge for quarterly clients, since it is readily available.
          */}
        {dueNext && (
          <p className={classes.nextCharge}>
            {`Your next charge of ${numeral(dueNext.charge.attr('amountInCentsAfterDiscounts')).divide(100).format('$0,0[.]00')} will be billed to the card on file on ${moment.utc(billingInformationResource.attr('anchorDate')).format('MM/DD')}.`}
          </p>
        )}
        <TextInput
          label="Name on Card"
          placeholder="Name on Card"
          name="name"
          value={name}
          onChange={this.updateState}
        />
        <StripeCardElement
          test="stripe-input"
          style={{ base: { fontSize: '16px', border: '1px solid black' } }}
        />
        <div className="authorization">
          <div className="check-option">
            <Checkbox
              test="authorize-payment"
              name="authorization"
              checked={authorized}
              onChange={() => (this.setState(state => ({
                authorized: !state.authorized,
              })))}
            />
            <p>
              I authorize Facet to charge the above account in
              accordance with the Facet Financial Planning and
              Investment Management Agreement.
            </p>
          </div>
        </div>
        <Button
          color="secondary"
          disabled={processing || !authorized}
          disableElevation
          fullWidth
          onClick={this.onCompleteSignUp}
          startIcon={<FontAwesome icon="lock" />}
          variant="contained"
        >
          {`Pay ${numeral(dueNow.charge.attr('amountInCentsAfterDiscounts')).divide(100).format('$0,0[.]00')}`}
        </Button>
        <Button
          color="primaryGray"
          className="credit-card-cancel-button"
          disabled={processing}
          fullWidth
          onClick={() => history.push(dashboardUri)}
        >
          {'Cancel'}
        </Button>
      </Fragment>
    );
  }

  render() {
    const { classes, showCloseIcon } = this.props;

    return (
      <Dialog
        className="PaymentModal"
        open
        size="lg"
        showCloseIcon={showCloseIcon}
      >
        <DialogContent>
          <h3 className={classes.header}>Payment Details</h3>
          {this.isLoading() ? (
            <LoadingIndicator />
          ) : (
            this.renderContent()
          )}
        </DialogContent>
      </Dialog>
    );
  }
}

PaymentModal.contextType = SwaggerlessContext;

PaymentModal.propTypes = {
  stripe: PropTypes.object.isRequired,
  householdId: PropTypes.number.isRequired,
  householdResource: PropTypes.object,
  showCloseIcon: PropTypes.bool,
  subscriptionInfo: PropTypes.shape({
    attributes: PropTypes.shape({
      anchorDate: PropTypes.string,
      oneTimeFee: PropTypes.number,
      annualFee: PropTypes.number,
      subscriptionFee: PropTypes.number,
    }),
  }).isRequired,
  UserContext: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

PaymentModal.defaultProps = {
  householdResource: null,
  showCloseIcon: true,
};

// TODO: I don't think the component is ever used anymore.  It's not used on the legacy signup path, the C2B
//  signup path, or the member profile billing settings path.  I converted it just in case, but I think it
//  can be removed.
export default withUserContextAdapter(
  withHouseholdContextAdapter(
    injectStripe(withStyles(styles)(PaymentModal)),
  ),
);
